import * as React from 'react'
import {
  Box, Typography, Button, Chip, Alert, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, TablePagination,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'

import {sectionCardSx, dataGridWrapSx, headerCellSx, cellSx, primaryBtnSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../../styles/foundation/colors'

import {dims as fmtDims, grams, kg, int} from '../format'
import {RULES} from '../data/reference'
import {PACK_KIND, PACK_KIND_LABEL, packUsage, brandingLabel} from './state/packingModel'
import EmptyState from '../components/EmptyState'
import {chipSx} from '../components/tone'
import PackDrawer from './PackDrawer'
import PackRemoveDrawer from './PackRemoveDrawer'

const PER_PAGE = 12

const panelAlertSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 0.75, '& .MuiAlert-message': {lineHeight: 1.6}}

const KIND_ICON = {
  [PACK_KIND.CARTON]: Inventory2OutlinedIcon,
  [PACK_KIND.FLYER]: MailOutlineIcon,
  [PACK_KIND.OTHER]: CategoryOutlinedIcon,
}

/**
 * "N types" chip tone: `warn` above the workable band, `neutral` below it,
 * `ok` inside it. A tone NAME from `components/tone.js`, resolved through
 * `chipSx` — never a hardcoded colour.
 */
export function bandTone(count, band) {
  if (count > band[1]) return 'warn'
  if (count < band[0]) return 'neutral'
  return 'ok'
}

/**
 * MUI's own `TableSortLabel` default is ascending on a fresh column; this
 * module's own convention (see `SubcategoryPanel.js`'s `nextSort`) inverts
 * that, so the first click on any column shows its largest value first.
 */
export function nextSort(prev, key) {
  return prev.key === key ? {key, dir: prev.dir === 'asc' ? 'desc' : 'asc'} : {key, dir: 'desc'}
}

/**
 * One row per packing material, with every value a comparator will need
 * pre-computed — `volProduct` (the L x B x H product Dimensions sorts on,
 * NOT the formatted string) and `usageSkus`/`usageCombos` (`packUsage`'s
 * own split). Materialised here, once, rather than inside the sort
 * comparator: the map's own R1 warns against recomputing a derived
 * accessor per comparison, which on a table this size would call
 * `packUsage` — an O(skus + combos) scan — twice per pair compared.
 */
export function buildRows(state) {
  return (state.packs || []).map((p) => {
    const usage = packUsage(state, p.id)
    const triple = p.dims
    return {
      ...p,
      usageSkus: usage.skus,
      usageCombos: usage.combos,
      volProduct: triple ? triple[0] * triple[1] * triple[2] : -1,
    }
  })
}

/** Volumetric cell: an em dash for "no dimensions", never a false 0.00 kg. */
function volCell(volKg) {
  return volKg == null ? '—' : kg(volKg)
}

/**
 * The packing-material panel — Packing Task 6. `state`/`actions` are the
 * narrow slice and action set `PackingPage` threads down from the one
 * module-wide store (see that file's own header); this panel holds no
 * material data itself, only which drawer is open and the table's own
 * sort/page — every actual mutation goes through `actions.upsertPack`/
 * `actions.removePack`, which already implement the cascade.
 */
export default function PackingPanel({state, actions, focusFallbackRef}) {
  const {packs} = state
  const band = RULES.PACKING_MATERIAL_BAND

  const [page, setPage] = React.useState(0)
  const [sort, setSort] = React.useState({key: null, dir: 'desc'})
  const [editPack, setEditPack] = React.useState(null) // the pack row, or null for "add"
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const [removePack, setRemovePack] = React.useState(null)

  const rows = React.useMemo(() => buildRows(state), [state])

  const sortedRows = React.useMemo(() => {
    if (!sort.key) return rows
    const decorated = rows.map((r) => ({r, key: r[sort.key]}))
    decorated.sort((a, b) => {
      if (a.key < b.key) return sort.dir === 'asc' ? -1 : 1
      if (a.key > b.key) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return decorated.map((d) => d.r)
  }, [rows, sort])

  const handleSort = (key) => setSort((prev) => nextSort(prev, key))

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PER_PAGE))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = sortedRows.slice(clampedPage * PER_PAGE, clampedPage * PER_PAGE + PER_PAGE)

  // Same focus-recovery fallback CataloguePage's/SubcategoryPanel's own
  // drawers use — reused, not invented a second time. A row's own
  // "Remove" button vanishes once the removal actually lands.
  const focusFallback = React.useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    window.requestAnimationFrame(() => {
      if (document.activeElement === document.body && focusFallbackRef && focusFallbackRef.current) {
        focusFallbackRef.current.focusActive()
      }
    })
  }, [focusFallbackRef])

  const closePackDrawer = React.useCallback(() => {
    setDrawerOpen(false)
    focusFallback()
  }, [focusFallback])

  const closeRemoveDrawer = React.useCallback(() => {
    setRemovePack(null)
    focusFallback()
  }, [focusFallback])

  const openAdd = () => { setEditPack(null); setDrawerOpen(true) }
  const openEdit = (row) => { setEditPack(row); setDrawerOpen(true) }

  const handleSave = (patch) => {
    actions.upsertPack(patch)
    closePackDrawer()
  }
  const handleRemove = (id, destId) => {
    actions.removePack(id, destId)
    closeRemoveDrawer()
  }

  const packTone = bandTone(packs.length, band)

  return (
    <Box sx={sectionCardSx}>
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, justifyContent: 'space-between', alignItems: {xs: 'flex-start', sm: 'center'}, gap: 1.5, mb: 1.5}}>
        <Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
              Packing material
            </Typography>
            <Chip size='small' label={`${int(packs.length)} types`} sx={chipSx(packTone)} />
          </Box>
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25, maxWidth: 640}}>
            Brands typically carry only {band[0]} to {band[1]} types of packing material, which is what makes a
            freeze workable: a short list is a list the packing floor can actually follow. A carton's
            dimensions set the volumetric weight of everything packed in it.
          </Typography>
        </Box>
        <Button sx={primaryBtnSx} startIcon={<AddIcon sx={{fontSize: 16}} />} onClick={openAdd}>
          Add material
        </Button>
      </Box>

      {packs.length > band[1] && (
        <Alert severity='warning' sx={{...panelAlertSx, mb: 1.5}}>
          This account carries {int(packs.length)} packing materials, above the {band[0]}-{band[1]} band that
          keeps a list the packing floor can actually follow. Past that band, materials get mixed up on the
          floor and the wrong material goes out on the shipment — which the freeze does not cover.
        </Alert>
      )}

      {packs.length === 0 ? (
        <EmptyState
          icon={Inventory2OutlinedIcon}
          title='No packing material on this account'
          body='Add the flyers and cartons your packing floor uses. Volumetric weight is anchored to them.'
          action={<Button sx={primaryBtnSx} startIcon={<AddIcon sx={{fontSize: 16}} />} onClick={openAdd}>Add material</Button>}
        />
      ) : (
        <Box sx={dataGridWrapSx}>
          <Table sx={{minWidth: 880}}>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>
                  <TableSortLabel active={sort.key === 'label'} direction={sort.key === 'label' ? sort.dir : 'desc'} onClick={() => handleSort('label')}>
                    Material
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={headerCellSx}>Branding</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
                  <TableSortLabel active={sort.key === 'volProduct'} direction={sort.key === 'volProduct' ? sort.dir : 'desc'} onClick={() => handleSort('volProduct')}>
                    Dimensions
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Volumetric</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Self-weight</TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>
                  <TableSortLabel active={sort.key === 'usageSkus'} direction={sort.key === 'usageSkus' ? sort.dir : 'desc'} onClick={() => handleSort('usageSkus')}>
                    SKUs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => {
                const Icon = KIND_ICON[row.kind] || CategoryOutlinedIcon
                const lastMaterial = packs.length === 1

                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={cellSx}>
                      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                        {row.label}
                      </Typography>
                      <Typography sx={{fontFamily: 'monospace', fontSize: SIZE['2xs'], color: SUBTLE}}>
                        {row.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {/* An empty chip is a grey pill that says nothing; the product master has no
                          branding column, so a live pack has none to show. */}
                      {brandingLabel(row.branding) ? (
                        <Chip
                          size='small'
                          label={brandingLabel(row.branding)}
                          sx={chipSx(row.branding === 'self' ? 'info' : 'neutral')}
                        />
                      ) : (
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <Icon sx={{fontSize: 16, color: SUBTLE}} />
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>
                          {PACK_KIND_LABEL[row.kind] || row.kind}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                        {fmtDims(row.dims)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                        {volCell(row.volKg)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums'}}>
                        {grams(row.selfWeightG)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      {row.usageSkus === 0 ? (
                        <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE}}>Unused</Typography>
                      ) : (
                        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>
                          {int(row.usageSkus)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{...cellSx, textAlign: 'right'}}>
                      <Box sx={{display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap'}}>
                        <Button
                          size='small'
                          sx={secondaryBtnSx}
                          aria-label={`Edit ${row.label}`}
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        {lastMaterial ? (
                          <Tooltip title='This is the only packing material on the account. Add another before removing it.'>
                            <Box
                              component='span'
                              tabIndex={0}
                              aria-label='Remove is unavailable. This is the only packing material on the account.'
                            >
                              <Button size='small' disabled sx={secondaryBtnSx}>Remove</Button>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Button
                            size='small'
                            sx={secondaryBtnSx}
                            aria-label={`Remove ${row.label}`}
                            onClick={() => setRemovePack(row)}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>

          <TablePagination
            component='div'
            rowsPerPageOptions={[PER_PAGE]}
            count={sortedRows.length}
            rowsPerPage={PER_PAGE}
            page={clampedPage}
            onPageChange={(e, next) => setPage(next)}
            sx={{
              '& .MuiTablePagination-toolbar': {fontFamily: FONT},
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {fontFamily: FONT, fontSize: SIZE['2xs']},
            }}
          />
        </Box>
      )}

      <PackDrawer
        open={drawerOpen}
        state={state}
        pack={editPack}
        onClose={closePackDrawer}
        onSave={handleSave}
      />

      <PackRemoveDrawer
        open={removePack !== null}
        state={state}
        pack={removePack}
        onClose={closeRemoveDrawer}
        onRemove={handleRemove}
      />
    </Box>
  )
}
